import { scheduleApi } from '../../api/endpoints.ts';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';
import type { Appointment } from '@shared/types/index.ts';

/**
 * A refusal raised in the browser, worded for the person who clicked.
 *
 * The shared `onError` handlers show an `ApiRequestError`'s message because
 * the server writes those to be read, and replace anything else with a
 * generic line. The proposal races are neither a server answer nor a fault:
 * they are expected outcomes with sentences written for them, so they get a
 * type that says which of the two they are.
 */
export class ExplainedRefusal extends Error {}

/** The request moved on before this click: a colleague answered it, or the customer asked again. */
export class RequestAlreadyHandled extends ExplainedRefusal {}

/**
 * The time the customer asked for, in words, or null when they typed a
 * message instead. Both halves or neither — a date with no time is not a
 * move anybody can make.
 */
export function proposedTimeLabel(appointment: Appointment): string | null {
  return appointment.proposedScheduledDate && appointment.proposedStartTimeLocal
    ? `${formatShortDate(appointment.proposedScheduledDate)} ${formatLocalTime(appointment.proposedStartTimeLocal)}`
    : null;
}

/**
 * An hour that has been and gone while nobody answered.
 *
 * Accepting is an ordinary reschedule underneath, and the reschedule schema
 * accepts a past date — a visit that has to be moved to yesterday to square
 * the books is a real thing. So nothing downstream would stop one click
 * moving an open visit into the past and clearing the customer's request as
 * though it had been dealt with. The instant is compared rather than the
 * local halves, because the reader's clock is not the customer's.
 *
 * Ask it at render to decide what to draw, and AGAIN at the click: a panel
 * left open across the deadline never re-renders.
 */
export function proposalHasPassed(appointment: Appointment): boolean {
  return appointment.proposedStartsAt !== null && new Date(appointment.proposedStartsAt).getTime() <= Date.now();
}

/**
 * The customer picked a time off their own page; this accepts it.
 *
 * The ordinary reschedule endpoint, with the proposal's date and time — not a
 * shortcut past it. Everything that guards a move still runs: the
 * double-booking check, the crew's external calendars, the confirmation
 * reset, the calendar re-sync. A slot taken between the customer's tap and
 * this click comes back as the same conflict any other move would raise,
 * which is the answer the office needs.
 *
 * `onStale` runs when the request turns out to have moved on, so the caller
 * can refresh what it is showing before the refusal is read.
 */
export async function acceptProposedTime(
  appointment: Appointment,
  messages: { passed: string; handled: string },
  onStale: () => void,
): Promise<Appointment> {
  // The deadline, re-read at the moment of the click rather than at the last
  // render. See `proposalHasPassed`.
  if (proposalHasPassed(appointment)) throw new ExplainedRefusal(messages.passed);

  // And the request itself, re-read from the server before it is acted on.
  // Two people can have this visit open; whoever gets there first confirms
  // or reschedules it, which clears the request and the proposal — and the
  // other screen is still holding the values it rendered minutes ago. The
  // reschedule endpoint has no opinion about that: it would happily move an
  // already-handled visit to the time somebody else has already dealt with.
  //
  // Compared on the request's own stamp rather than on the hour, because a
  // customer who asks twice can land on the same time for a different
  // request, and it is the request being accepted here.
  const current = await scheduleApi.appointment(appointment.id);
  if (
    current.changeRequestedAt !== appointment.changeRequestedAt ||
    current.proposedScheduledDate !== appointment.proposedScheduledDate ||
    current.proposedStartTimeLocal !== appointment.proposedStartTimeLocal
  ) {
    onStale();
    throw new RequestAlreadyHandled(messages.handled);
  }

  return scheduleApi.reschedule(appointment.id, {
    scheduledDate: appointment.proposedScheduledDate ?? appointment.scheduledDate,
    startTimeLocal: appointment.proposedStartTimeLocal ?? appointment.startTimeLocal,
    // And the same stamp again, for the server to check inside the write
    // itself. The re-read above narrows the window; it cannot close it,
    // because a colleague can still land between that GET and this PATCH —
    // and the PATCH on its own says only "move this visit", not "move it
    // because THIS request asked". With the stamp in the WHERE clause the two
    // are one statement, so a request handled in the gap takes the row out of
    // the guard rather than being quietly overwritten.
    expectedChangeRequestedAt: appointment.changeRequestedAt ?? undefined,
  });
}
